/**
 * Product Matcher Service
 * 
 * Matches products from internal catalog to user profile.
 * Used to inject relevant Heseda products into AI prompts
 * for contextual recommendations.
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import type { MasterProfile, ProductSource } from '../types/profile';
import type { NormalizedProduct } from './catalogService';
import { getCatalog } from './catalogService';
import type { WebSearchResultItem } from '../types/conversation';

/**
 * Korean-to-English concern mapping for profile matching
 */
const CONCERN_MAP: Record<string, string[]> = {
  // Pore concerns
  '모공': ['pore', 'pore-care', 'pores', 'pore minimizer'],
  '모공축소': ['pore', 'pore-care', 'pore minimizer'],
  // Acne concerns
  '여드름': ['acne', 'acne-care', 'blemish', 'breakout'],
  '트러블': ['acne', 'blemish', 'trouble', 'breakout'],
  // Wrinkle/aging concerns
  '주름': ['wrinkle', 'anti-aging', 'firming', 'aging'],
  '주름개선': ['anti-aging', 'wrinkle', 'firming'],
  '노화': ['anti-aging', 'aging', 'mature'],
  // Hydration concerns
  '건조': ['hydrating', 'moisture', 'dry', 'dehydration'],
  '수분부족': ['hydrating', 'moisture', 'hydration'],
  // Sensitivity concerns
  '민감성': ['sensitive', 'soothing', 'calming', 'barrier'],
  '자극': ['sensitive', 'gentle', 'soothing'],
  // Brightening concerns
  '미백': ['brightening', 'whitening', 'dark spot'],
  '색소침착': ['brightening', 'pigmentation', 'dark spot'],
  '다크스팟': ['dark spot', 'brightening', 'pigmentation'],
  // Sebum/oil concerns
  '피지': ['oil-control', 'sebum', 'oily'],
  '번들거림': ['oil-control', 'mattifying', 'shine'],
  // Blackhead concerns
  '블랙헤드': ['blackhead', 'pore-care', 'exfoliate'],
  // Eye concerns
  '다크서클': ['dark circle', 'eye', 'brightening'],
  '눈밑': ['eye', 'under-eye'],
  // General
  '복합성': ['combo', 'combination'],
  '중성': ['normal'],
  '건성': ['dry'],
  '지성': ['oily'],
};

/**
 * Translate Korean concerns to English keywords for matching
 */
function translateConcerns(concerns: string[]): string[] {
  const translated = new Set<string>();
  
  concerns.forEach(concern => {
    const lowerConcern = concern.toLowerCase();
    
    // Check if it's a Korean concern we can translate
    const mapping = CONCERN_MAP[lowerConcern];
    if (mapping) {
      mapping.forEach(eng => translated.add(eng));
    } else {
      // Keep original if no mapping (might be English already)
      translated.add(lowerConcern);
    }
  });
  
  return Array.from(translated);
}

/**
 * Translate Korean skin type to English
 */
function translateSkinType(skinType?: string): string | undefined {
  if (!skinType) return undefined;
  
  const typeMap: Record<string, string> = {
    '건성': 'dry',
    '지성': 'oily',
    '복합성': 'combo',
    '중성': 'normal',
    '민감성': 'sensitive',
  };
  
  return typeMap[skinType] || skinType.toLowerCase();
}

/**
 * Product match result with relevance scoring
 */
export interface ProductMatch {
  product: NormalizedProduct;
  score: number;           // 0-100 relevance score
  matchReasons: string[];  // Why this product matched
}

/**
 * Match configuration
 */
export interface MatchConfig {
  maxResults?: number;     // Default: 5
  minScore?: number;       // Minimum score to include (default: 30)
  boostPaidPlacement?: boolean;  // Boost isPaidPlacement products
  /** Optional embedding similarity map (productId → similarity boost 0-0.4) from RAG */
  embeddingSimilarityMap?: Map<string, number>;
}

const DEFAULT_CONFIG: Required<Omit<MatchConfig, 'embeddingSimilarityMap'>> = {
  maxResults: 5,
  minScore: 30,
  boostPaidPlacement: true,
};

/**
 * Calculate match score between a product and user profile
 * Higher scores indicate better matches
 */
export function calculateMatchScore(
  product: NormalizedProduct,
  profile: MasterProfile
): ProductMatch {
  let score = 0;
  const matchReasons: string[] = [];

  // Translate Korean values to English for matching
  const skinType = translateSkinType(profile.skin?.type);
  const rawConcerns = (profile.skin?.concerns || []);
  const concerns = translateConcerns(rawConcerns);
  const allergies = (profile.skin?.allergies || []).map(a => a.toLowerCase());
  const avoidIngredients = (profile.skin?.disliked || []).map(d => d.toLowerCase());
  const preferredBrands = (profile.skin?.preferredBrands || []).map(b => b.toLowerCase());
  const budget = profile.skin?.budgetMonthlyUSD;

  // 1. Skin type match (max 25 points)
  if (skinType && product.skinTypes.length > 0) {
    if (product.skinTypes.includes(skinType) || product.skinTypes.includes('all')) {
      score += 25;
      // Emotional, cosmetic-friendly descriptions
      const skinTypeMessages: Record<string, string> = {
        'oily': '번들거림 없이 산뜻하게',
        'dry': '푸석한 피부에 촉촉함을',
        'combo': '복합 피부의 균형을 맞춰요',
        'sensitive': '민감한 피부도 편안하게',
        'normal': '건강한 피부 유지에 딱!',
      };
      matchReasons.push(skinTypeMessages[skinType] || `${skinType} 피부에 적합`);
    }
  }

  // 2. Concerns match (max 30 points, 10 per concern up to 3)
  const matchedConcerns = concerns.filter(concern =>
    product.concernsMatch.some(c => c.includes(concern) || concern.includes(c))
  );
  if (matchedConcerns.length > 0) {
    score += Math.min(matchedConcerns.length * 10, 30);
    // Emotional concern descriptions - comprehensive mapping
    const concernPhrases: Record<string, string> = {
      // English keys
      'acne': '트러블 진정에 효과적',
      'acne-care': '트러블 케어',
      'pore': '모공을 깨끗하게 정돈',
      'pore-care': '모공 케어의 정석',
      'pores': '모공을 깨끗하게',
      'wrinkle': '탄력있는 피부결로',
      'anti-aging': '시간을 되돌리는 케어',
      'firming': '탄탄한 피부로',
      'hydrating': '속부터 차오르는 수분감',
      'moisture': '촉촉함이 가득',
      'brightening': '맑고 환한 피부톤',
      'sensitive': '자극 없이 순하게',
      'soothing': '민감 피부 진정',
      'dark spot': '잡티 없는 깨끗함',
      'oil-control': '하루종일 보송보송',
      'barrier': '피부 장벽 강화',
      'exfoliate': '부드러운 각질 케어',
    };
    // Get unique phrases, avoiding duplicates
    const phrases = new Set<string>();
    matchedConcerns.forEach(c => {
      const phrase = concernPhrases[c];
      if (phrase) phrases.add(phrase);
    });
    if (phrases.size > 0) {
      matchReasons.push(Array.from(phrases).slice(0, 2).join(', '));
    }
  }

  // 3. Preferred brand bonus (10 points)
  if (preferredBrands.length > 0) {
    const brandMatch = preferredBrands.some(b => 
      product.brand.toLowerCase().includes(b) || b.includes(product.brand.toLowerCase())
    );
    if (brandMatch) {
      score += 10;
      matchReasons.push(`From your preferred brand: ${product.brand}`);
    }
  }

  // 4. Price within budget (10 points)
  if (budget && product.priceUSD) {
    // Assume monthly budget, product is ~1/4 of budget is reasonable
    if (product.priceUSD <= budget * 0.5) {
      score += 10;
      matchReasons.push('Within your budget');
    }
  }

  // 5. Popularity bonus (max 10 points)
  if (product.popularityScore) {
    score += Math.min(product.popularityScore / 100, 10);
  }

  // 6. Paid placement boost (15 points if enabled)
  if (product.isPaidPlacement) {
    score += 15;
    matchReasons.push('✨ 에디터 추천');
  }

  // 7. In stock bonus (5 points)
  if (product.inStock) {
    score += 5;
  }

  // PENALTIES

  // Allergen penalty (-50 points, essentially disqualifies)
  const allergenMatch = allergies.some(allergy =>
    product.allergens.includes(allergy) ||
    (product.ingredientsFull?.toLowerCase().includes(allergy))
  );
  if (allergenMatch) {
    score -= 50;
    matchReasons.push('⚠️ Contains allergen you should avoid');
  }

  // Avoided ingredient penalty (-30 points)
  const avoidMatch = avoidIngredients.some(avoid =>
    product.keyIngredients.includes(avoid) ||
    (product.ingredientsFull?.toLowerCase().includes(avoid))
  );
  if (avoidMatch) {
    score -= 30;
  }

  // AvoidIf match penalty (e.g., pregnancy)
  if (profile.identity?.pregnant && product.avoidIf.includes('pregnancy')) {
    score -= 50;
    matchReasons.push('⚠️ Not recommended during pregnancy');
  }

  return {
    product,
    score: Math.max(0, Math.min(100, score)),
    matchReasons,
  };
}

/**
 * Find matching products from catalog based on user profile
 * Returns products sorted by relevance score
 */
export function findMatchingProducts(
  catalog: NormalizedProduct[],
  profile: MasterProfile,
  config: MatchConfig = {}
): ProductMatch[] {
  const { maxResults, minScore, boostPaidPlacement, embeddingSimilarityMap } = { ...DEFAULT_CONFIG, ...config };

  // Calculate scores for all products
  const scored = catalog
    .filter(p => p.inStock !== false) // Only in-stock products
    .map(product => {
      const match = calculateMatchScore(product, profile);
      // Apply RAG vector similarity boost if available (up to +40 points)
      if (embeddingSimilarityMap) {
        const productId = product.id || product.sku || '';
        const vectorBoost = embeddingSimilarityMap.get(productId) || 0;
        if (vectorBoost > 0) {
          const boostPoints = Math.round(vectorBoost * 100);
          match.score = Math.min(100, match.score + boostPoints);
          match.matchReasons.push(`📊 AI 매칭 +${boostPoints}pt`);
        }
      }
      return match;
    })
    .filter(match => match.score >= minScore);

  // Sort by score (descending), with paid placements first if boosted
  scored.sort((a, b) => {
    if (boostPaidPlacement) {
      // Paid placements first, then by score
      if (a.product.isPaidPlacement && !b.product.isPaidPlacement) return -1;
      if (!a.product.isPaidPlacement && b.product.isPaidPlacement) return 1;
    }
    return b.score - a.score;
  });

  return scored.slice(0, maxResults);
}

/**
 * Generate a unique, product-specific reason for the recommendation
 * This is the PRIMARY display text shown on the product card
 */
function generateProductReason(product: NormalizedProduct, matchReasons: string[]): string {
  // Priority 1: Ingredient-based benefit (most specific)
  if (product.keyIngredients.length > 0) {
    const ingredientBenefits: Record<string, string> = {
      'niacinamide': '나이아신아마이드로 피부결 개선',
      'hyaluronic acid': '히알루론산으로 깊은 보습',
      'retinol': '레티놀로 피부 탄력 케어',
      'vitamin c': '비타민C로 맑은 피부톤',
      'ceramides': '세라마이드로 장벽 강화',
      'salicylic acid': '살리실산으로 모공 케어',
      'snail mucin': '달팽이 뮤신으로 피부 재생',
      'centella': '센텔라로 진정 케어',
      'tea tree': '티트리로 트러블 진정',
      'galactomyces': '갈락토미세스로 피부 광채',
      'peptides': '펩타이드로 탄력 개선',
      'aha': 'AHA로 부드러운 각질 케어',
      'bha': 'BHA로 모공 속 클렌징',
      'green tea': '녹차 성분으로 항산화 케어',
      'squalane': '스쿠알란으로 수분 밀봉',
      'panthenol': '판테놀로 피부 진정',
      'allantoin': '알란토인으로 피부 보호',
      'madecassoside': '마데카소사이드로 재생 케어',
      'adenosine': '아데노신으로 주름 개선',
      'arbutin': '알부틴으로 브라이트닝',
      // Heseda catalog ingredients
      'ascorbic acid': '비타민C(아스코르빈산)로 브라이트닝',
      'protopanaxadiol': 'PPD 진세노사이드로 피부 재생',
      'lactic acid': '락틱애시드로 부드러운 각질 케어',
      'glycolic acid': '글리콜산으로 피부결 개선',
      'ginseng': '인삼 성분으로 활력 케어',
      'glutathione': '글루타치온으로 맑은 피부톤',
      'beta-glucan': '베타글루칸으로 피부 진정',
      'sodium hyaluronate': '히알루론산으로 깊은 보습',
      'tocopherol': '비타민E로 항산화 케어',
      'ferulic acid': '페룰산으로 항산화 방어',
      'atelocollagen': '아텔로콜라겐으로 탄력 케어',
      'ascorbyl glucoside': '비타민C 유도체로 브라이트닝',
      'citric acid': '시트르산으로 피부 톤업',
    };
    
    const firstIngredient = product.keyIngredients[0].toLowerCase();
    if (ingredientBenefits[firstIngredient]) {
      return ingredientBenefits[firstIngredient];
    }
  }
  
  // Priority 2: Category + brand combination
  const categoryReasons: Record<string, string[]> = {
    'cleanser': ['순한 클렌징으로 피부결 정돈', '깨끗한 세안의 시작', '저자극 폼으로 산뜻하게'],
    'toner': ['피부 밸런스 첫 단계', '촉촉한 토너로 피부 정돈', '수분 토너로 기초 케어'],
    'essence': ['깊은 영양 에센스', '피부 속까지 영양 공급', '에센스로 피부 광채'],
    'serum': ['집중 케어 세럼', '고농축 세럼으로 피부 개선', '타겟 케어 세럼'],
    'moisturizer': ['촉촉한 보습 마무리', '수분 크림으로 피부 보호', '보습 장벽 케어'],
    'cream': ['크림으로 영양 마무리', '리치한 크림 케어', '피부 보호막 형성'],
    'sunscreen': ['자외선 차단 필수템', 'UV 차단으로 피부 보호', '가벼운 선케어'],
    'mask': ['스페셜 집중 케어', '마스크로 피부 회복', '주 1-2회 스페셜 케어'],
    'eye': ['눈가 전용 케어', '아이크림으로 눈가 탄력', '눈가 주름 집중 케어'],
  };
  
  const category = product.category?.toLowerCase() || '';
  if (categoryReasons[category]) {
    // Use product name hash to pick a consistent but varied message
    const hash = product.name?.charCodeAt(0) || 0;
    const options = categoryReasons[category];
    return options[hash % options.length];
  }
  
  // Priority 3: Use concern-based reason from matchReasons (not skin type)
  const concernReason = matchReasons.find(r => 
    !r.includes('피부에 적합') &&
    !r.includes('유지에 딱') &&
    !r.includes('피부도 편안') &&
    !r.includes('균형을') &&
    !r.includes('촉촉함을') &&
    !r.includes('산뜻하게') &&
    !r.includes('에디터') &&
    !r.includes('budget') &&
    !r.includes('brand')
  );
  if (concernReason) {
    return concernReason;
  }
  
  // Fallback: Generic but varied based on product
  const fallbacks = [
    '피부 고민에 맞춤 추천',
    '프로필 기반 맞춤 제품',
    '당신을 위한 스킨케어',
    '피부 타입에 딱 맞는 제품',
  ];
  const hash = (product.id?.charCodeAt(0) || 0) + (product.name?.charCodeAt(0) || 0);
  return fallbacks[hash % fallbacks.length];
}

/**
 * Generate a unique, product-specific snippet based on product data and match reasons
 */
function generateProductSnippet(product: NormalizedProduct, reasons: string[]): string {
  const parts: string[] = [];
  
  // Add key ingredient highlight if available
  if (product.keyIngredients.length > 0) {
    const ingredientKo: Record<string, string> = {
      'niacinamide': '나이아신아마이드',
      'hyaluronic acid': '히알루론산',
      'retinol': '레티놀',
      'vitamin c': '비타민C',
      'ceramides': '세라마이드',
      'salicylic acid': '살리실산',
      'snail mucin': '달팽이 뮤신',
      'centella': '센텔라',
      'tea tree': '티트리',
      'galactomyces': '갈락토미세스',
      'peptides': '펩타이드',
      'aha': 'AHA',
      'bha': 'BHA',
      'green tea': '녹차',
      'squalane': '스쿠알란',
      // Heseda catalog ingredients
      'ascorbic acid': '비타민C(아스코르빈산)',
      'protopanaxadiol': 'PPD 진세노사이드',
      'lactic acid': '락틱애시드',
      'glycolic acid': '글리콜산',
      'ginseng': '인삼',
      'glutathione': '글루타치온',
      'beta-glucan': '베타글루칸',
      'sodium hyaluronate': '히알루론산나트륨',
      'tocopherol': '비타민E',
      'ferulic acid': '페룰산',
      'panthenol': '판테놀',
      'allantoin': '알란토인',
      'madecassoside': '마데카소사이드',
      'atelocollagen': '아텔로콜라겐',
      'ascorbyl glucoside': '비타민C 유도체',
      'citric acid': '시트르산',
    };
    const firstIngredient = product.keyIngredients[0].toLowerCase();
    const koIngredient = ingredientKo[firstIngredient] || product.keyIngredients[0];
    parts.push(`${koIngredient} 함유`);
  }
  
  // Use product tags for specific, differentiated descriptions
  const tagMessages: Record<string, string> = {
    'brightening': '브라이트닝 케어',
    'hydrating': '집중 보습 케어',
    'anti-aging': '안티에이징 케어',
    'pore-care': '모공 집중 케어',
    'exfoliating': '부드러운 각질 관리',
    'soothing': '민감 피부 진정',
    'firming': '탄력 집중 케어',
    'barrier-repair': '피부 장벽 강화',
    'vitamin-c': '비타민C 집중 케어',
    'antioxidant': '항산화 보호',
    'collagen-boost': '콜라겐 부스팅',
    'oil-control': '유분 밸런스 케어',
    'acne-care': '트러블 케어',
    'revitalizing': '피부 활력 충전',
    'nourishing': '깊은 영양 공급',
    'balancing': '피부 밸런스 케어',
    'elasticity': '탄력 개선',
    'hyperpigmentation': '색소 침착 케어',
  };
  let hasTagMatch = false;
  if (product.tags && product.tags.length > 0) {
    const tagMsg = product.tags
      .map(tag => tagMessages[tag])
      .find(msg => msg !== undefined);
    if (tagMsg) {
      parts.push(tagMsg);
      hasTagMatch = true;
    }
  }
  // Fallback to category only when no tag matched
  if (!hasTagMatch) {
    const categoryMessages: Record<string, string> = {
      'cleanser': '순한 세안으로 피부결 정돈',
      'toner': '첫 단계부터 피부 밸런스 케어',
      'essence': '에센스로 깊은 영양 공급',
      'serum': '집중 케어로 피부 고민 해결',
      'moisturizer': '수분 크림으로 보습 마무리',
      'sunscreen': '자외선 차단으로 피부 보호',
      'mask': '스페셜 케어로 집중 관리',
      'eye': '눈가 전용 케어',
      'cream': '크림으로 촉촉한 마무리',
    };
    const category = product.category?.toLowerCase() || '';
    if (categoryMessages[category]) {
      parts.push(categoryMessages[category]);
    }
  }
  
  // Add first non-duplicate reason
  const firstReason = reasons.find(r => 
    !r.includes('에디터 추천') && 
    !r.includes('Within your budget') &&
    !r.includes('preferred brand')
  );
  if (firstReason && parts.length < 2) {
    parts.push(firstReason);
  }
  
  return parts.length > 0 ? parts.join(' · ') : (reasons[0] || '추천 제품');
}

/**
 * Convert matched products to WebSearchResultItem format
 * for unified display in CollapsibleWebResults
 */
export function matchesToWebResults(
  matches: ProductMatch[],
  source: ProductSource = 'inhouse'
): WebSearchResultItem[] {
  return matches.map(match => ({
    id: match.product.id || match.product.sku, // Product ID for cart/wishlist
    link: match.product.url || match.product.productUrl || '#',
    name: match.product.name,
    image: match.product.image || match.product.imageUrl,
    price: match.product.priceUSD || match.product.price,
    currency: match.product.currency || 'USD',
    snippet: generateProductSnippet(match.product, match.matchReasons),
    displayLink: match.product.brand,
    reason: generateProductReason(match.product, match.matchReasons), // Unique per product
    source,
    isPaidPlacement: match.product.isPaidPlacement, // For Editor Pick badge
  }));
}

/**
 * Build AI context string for matched products
 * This is injected into the OpenAI prompt for contextual recommendations
 */
export function buildProductContextForAI(
  matches: ProductMatch[],
  language: string = 'en'
): string {
  if (matches.length === 0) return '';

  const t = (ko: string, en: string) => language === 'ko' ? ko : en;

  const header = t(
    'Heseda 추천 제품 (사용자 프로필 기반):',
    'Heseda Recommended Products (based on user profile):'
  );

  const productLines = matches.map((match, i) => {
    const p = match.product;
    const price = p.priceUSD ? `$${p.priceUSD}` : '';
    const reasons = match.matchReasons.slice(0, 2).join('; ');
    return `${i + 1}. ${p.title} ${price} - ${reasons}`;
  });

  return `
${header}
${productLines.join('\n')}

${t(
  '위 제품들이 사용자에게 적합할 경우 자연스럽게 추천해주세요.',
  'Recommend these products naturally if relevant to the user\'s needs.'
)}
`.trim();
}

/**
 * Match products from the default catalog to a user profile.
 * Loads the catalog, computes match scores, and returns top matches.
 * This is the main entry point used by useChatApi.
 */
export async function matchProductsToProfile(
  profile: MasterProfile,
  maxResults: number = 5
): Promise<ProductMatch[]> {
  const catalog = await getCatalog();
  return findMatchingProducts(catalog, profile, { maxResults });
}

/**
 * Normalize title for deduplication (remove brand, special chars, lowercase)
 */
function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9가-힣\s]/g, '') // Remove special chars
    .replace(/\s+/g, ' ')            // Normalize spaces
    .trim();
}

/**
 * Merge internal product matches with web search results
 * Internal products are prioritized and shown first
 * Deduplicates by normalized title to avoid showing same product twice
 */
export function mergeProductSources(
  internalMatches: ProductMatch[],
  webResults: WebSearchResultItem[],
  maxTotal: number = 6
): WebSearchResultItem[] {
  const internal = matchesToWebResults(internalMatches, 'inhouse');
  
  // Track seen titles for deduplication
  const seenTitles = new Set<string>();
  
  // Add internal product titles to seen set
  internal.forEach(item => {
    if (item.name) seenTitles.add(normalizeTitle(item.name));
  });
  
  // Filter web results to exclude duplicates
  const web = webResults
    .map(r => ({ ...r, source: r.source || 'web' as ProductSource }))
    .filter(r => {
      if (!r.name) return true; // Keep items without name
      const normalized = normalizeTitle(r.name);
      if (seenTitles.has(normalized)) return false; // Skip duplicate
      seenTitles.add(normalized);
      return true;
    });
  
  // Internal first, then deduplicated web, up to maxTotal
  const merged = [...internal, ...web];
  return merged.slice(0, maxTotal);
}
